/** The wizard's form styling, in one place.
 *
 *  `EditClientDialog.js` and the NewClient dialog this wizard replaced each declare their own
 *  `fieldSx`/`labelSx`/`selectSx` at the top of the file, which is the established idiom for a
 *  client-scoped screen — but theirs are built from `ui/colors.js`, whose `primary` is #57837E
 *  rather than the brand green. These are the same presets rebuilt on `coachTokens`, so the wizard
 *  matches `Clients.js` and `ClientPageV2` on either side of it.
 *
 *  Deliberately NOT the `nutrition/v2/ui/primitives` controls: those are fine looking (their brand
 *  is imported from `coachTokens`, so there is no colour clash) but they are scoped to the nutrition
 *  feature, whose own header reserves the right to retune its neutrals without considering other
 *  screens. A clients screen should not be downstream of that. */

import type { SxProps, Theme } from "@mui/material";
import { t } from "../../dashboard/coach/coachTokens";

export const labelSx: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 500,
  color: t.t2,
  marginBottom: "6px",
};

export const hintSx: SxProps<Theme> = {
  fontSize: "11.5px",
  color: t.t3,
  marginTop: "5px",
  lineHeight: 1.45,
};

export const fieldSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    borderRadius: t.rs,
    backgroundColor: t.white,
    fontSize: "14px",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
};

export const selectSx: SxProps<Theme> = {
  borderRadius: t.rs,
  backgroundColor: t.white,
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" },
};

/** Number inputs, with the browser spinners removed — they misread as a stepper in a dense grid. */
export const numberFieldSx: SxProps<Theme> = {
  ...(fieldSx as object),
  "& input[type=number]": { MozAppearance: "textfield", fontVariantNumeric: "tabular-nums" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

/** A choice card — the big "existing user vs new client" / "apply a template vs skip" buttons. */
export const choiceCardSx = (selected: boolean): SxProps<Theme> => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "6px",
  padding: "16px",
  textAlign: "start",
  textTransform: "none",
  borderRadius: t.rs,
  border: `1px solid ${selected ? t.green : t.border}`,
  backgroundColor: selected ? t.greenLight : t.white,
  color: t.text,
  boxShadow: "none",
  "&:hover": {
    borderColor: t.green,
    backgroundColor: selected ? t.greenLight : t.bg,
    boxShadow: "none",
  },
});
